import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Video, Calendar, ExternalLink, Bell, BellRing } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function LiveSessionsPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);

  const load = useCallback(() => {
    api.get("/live-sessions").then(({ data }) => setSessions(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load, user]);

  const toggleRemind = async (s) => {
    if (!user) { navigate("/auth"); return; }
    if (s.registered) {
      await api.delete(`/live-sessions/${s.session_id}/register`);
      toast.info("Rappel désactivé");
    } else {
      await api.post(`/live-sessions/${s.session_id}/register`);
      toast.success("Rappel activé — emails 24 h et 1 h avant la session");
    }
    load();
  };

  const fmt = (d) =>
    new Date(d).toLocaleDateString(i18n.language === "en" ? "en-CA" : "fr-CA", {
      weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
    });

  return (
    <main className="max-w-4xl mx-auto px-6 py-16 relative z-10" data-testid="live-sessions-page">
      <h1 className="text-4xl sm:text-5xl font-serif-display tracking-tight">{t("live.title")}</h1>
      <p className="text-foreground/65 mt-3">{t("live.subtitle")}</p>

      <div className="mt-12 space-y-6">
        {sessions.map((s) => (
          <div key={s.session_id} className="card-lift bg-card border border-primary/15 rounded-lg p-7 flex flex-col md:flex-row md:items-center gap-6" data-testid={`live-session-${s.session_id}`}>
            <div className="flex-1">
              <p className="text-xs text-primary uppercase tracking-widest flex items-center gap-2">
                <Calendar size={13} /> {fmt(s.date)} · {s.duration}
              </p>
              <h3 className="font-serif-display text-2xl mt-2">{s.title}</h3>
              <p className="text-sm text-foreground/60 mt-2 leading-relaxed">{s.description}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <button onClick={() => toggleRemind(s)}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm transition-colors duration-200 ${s.registered ? "bg-primary/15 text-primary border border-primary/40" : "border border-primary/25 text-foreground/70 hover:bg-primary/10"}`}
                data-testid={`remind-btn-${s.session_id}`}>
                {s.registered ? <BellRing size={14} /> : <Bell size={14} />} {s.registered ? "Rappel activé" : "Me rappeler"}
              </button>
              <a href={s.zoom_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-primary/40 text-primary text-sm hover:bg-primary/10 transition-colors duration-200"
                data-testid={`zoom-link-${s.session_id}`}>
                <Video size={15} /> {t("live.join")} <ExternalLink size={12} />
              </a>
            </div>
          </div>
        ))}
      </div>
    </main>
  );
}
